import type { ReactNode } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { BsCheckCircle } from 'react-icons/bs';
import BkButton from '../../Components/ui/BkButton';
import ProfileAvatar from '../../Components/ProfileAvatar';
import SeatEmoteBurst from './SeatEmoteBurst';
import { getPlayerSkinDef, skinLabelStyle } from '../../player-colors';
import useGameInfo, { type UseGameInfoOptions } from './hooks/use-game-info';

type SeatPlayer = {
  displayName?: string;
  ready?: boolean;
  playerId?: string;
  colorId?: string;
  avatarId?: string;
  recentEmote?: { emoteId?: string; at?: number };
};

export type GameInfoPlayersProps = {
  game: UseGameInfoOptions['game'] & {
    firstPlayer?: SeatPlayer;
    secondPlayer?: SeatPlayer;
  };
  applyGameUpdate?: (payload: Record<string, unknown>) => void;
};

function SeatName({
  player,
  fallback,
}: {
  player?: SeatPlayer;
  fallback?: ReactNode;
}) {
  const labelCss = skinLabelStyle(getPlayerSkinDef(player?.colorId));
  const emote = player?.recentEmote;
  return (
    <div className="relative flex items-center gap-2 sm:gap-3">
      <span className="relative">
        <ProfileAvatar avatarId={player?.avatarId} size="sm" />
        <SeatEmoteBurst emoteId={emote?.emoteId} at={emote?.at} />
      </span>
      <div className="text-center font-semibold" style={labelCss}>
        {player?.displayName || fallback}
      </div>
    </div>
  );
}

export default function GameInfoPlayers({
  game,
  applyGameUpdate,
}: GameInfoPlayersProps) {
  const {
    firstPlayer,
    secondPlayer,
  } = game;

  const {
    isInGame,
    isLocalGame,
    leavingGame,
    actionButtonDisabled,
    actionButtonText,
    isReady,
    handleActionButtonOnClick,
    handleLeave,
  } = useGameInfo({ game, applyGameUpdate });

  const actionVariant = isInGame ? (isReady ? 'primary' : 'secondary') : 'primary';
  const firstLabelCss = skinLabelStyle(getPlayerSkinDef(firstPlayer?.colorId));
  const secondLabelCss = skinLabelStyle(getPlayerSkinDef(secondPlayer?.colorId));

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center space-y-4 text-xl sm:text-2xl md:text-3xl lg:text-4xl">
      {isLocalGame ? (
        <>
          <div className="text-center text-2xl font-bold uppercase tracking-wider text-bk-gold sm:text-3xl md:text-4xl lg:text-5xl">
            Local Game
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-6">
            <div className="flex items-center space-x-2">
              <div className="text-center font-semibold" style={firstLabelCss}>
                Player 1
              </div>
              <BsCheckCircle className="text-bk-gold" />
            </div>
            <div className="font-bold text-bk-muted">VS</div>
            <div className="flex items-center space-x-2">
              <div className="text-center font-semibold" style={secondLabelCss}>
                Player 2
              </div>
              <BsCheckCircle className="text-bk-gold" />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center space-x-2">
            <SeatName
              player={firstPlayer}
              fallback={
                <AiOutlineLoading3Quarters className="animate-spin text-bk-gold" />
              }
            />
            {firstPlayer?.ready ? <BsCheckCircle className="text-bk-gold" /> : null}
          </div>
          <div className="flex items-center">
            <div className="h-0.5 w-10 bg-bk-gold" />
            <div className="mx-4 font-bold text-bk-muted">VS</div>
            <div className="h-0.5 w-10 bg-bk-gold" />
          </div>
          <div className="flex items-center space-x-2">
            <SeatName
              player={secondPlayer}
              fallback={
                <AiOutlineLoading3Quarters className="animate-spin text-bk-gold" />
              }
            />
            {secondPlayer?.ready ? (
              <BsCheckCircle className="text-bk-gold" />
            ) : null}
          </div>
        </>
      )}
      {isInGame ? (
        <BkButton
          variant="danger"
          className="absolute top-2 right-4 md:top-4 md:right-8"
          onClick={() => void handleLeave()}
        >
          {leavingGame ? 'Leaving...' : 'Leave'}
        </BkButton>
      ) : null}
      <BkButton
        variant={actionVariant}
        size="lg"
        className="absolute bottom-2 right-4 md:bottom-4 md:right-8"
        disabled={actionButtonDisabled}
        onClick={() => void handleActionButtonOnClick()}
      >
        {actionButtonText}
      </BkButton>
    </div>
  );
}
